import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Package, Loader2, RefreshCw, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Affiche la liste des tâches actionnables pour le field operator.
 *
 * 2 types :
 *  - DLC périmée / proche  → "Retirer N barquettes de X (E12, E13)"
 *  - Réappro nécessaire    → "Réapprovisionner X — 60 places libres (0% rempli)"
 *
 * Trié par sévérité (critical > high > medium).
 */
export default function FieldTaskList({ limit = 10, showHeader = true, autoLoad = true }) {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ total: 0, critical: 0, high: 0, medium: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL"); // ALL | DLC | REAPPRO

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/distributors/field-tasks");
      setTasks(r.data.tasks || []);
      setStats(r.data.stats || {});
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoLoad) load();
  }, [autoLoad]);

  const filtered = filter === "ALL" ? tasks : tasks.filter((t) => t.type === filter);
  const visible = filtered.slice(0, limit);
  const truncated = filtered.length > limit;

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="field-task-list">
      {showHeader && (
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1F4E3D]" />
            <h2 className="font-heading text-base font-semibold text-[#1A1C1A]">
              Mes tâches du jour
            </h2>
            {stats.total > 0 && (
              <span className="bg-[#1F4E3D] text-white text-[10px] px-1.5 py-0.5 rounded-full font-mono">
                {stats.total}
              </span>
            )}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-xs text-[#575A57] hover:text-[#1F4E3D] flex items-center gap-1"
            data-testid="field-tasks-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "..." : "Rafraîchir"}
          </button>
        </div>
      )}

      {/* Filtres */}
      {tasks.length > 0 && (
        <div className="flex items-center gap-1 mb-3 text-xs">
          {[
            { v: "ALL", label: "Toutes", count: stats.total },
            { v: "DLC", label: "DLC", count: stats.dlc_count },
            { v: "REAPPRO", label: "Réappro", count: stats.reappro_count },
          ].map(({ v, label, count }) => (
            <button
              key={v}
              onClick={() => setFilter(v)}
              data-testid={`field-tasks-filter-${v.toLowerCase()}`}
              className={`px-2 py-1 rounded transition-colors flex items-center gap-1 ${
                filter === v
                  ? "bg-[#1F4E3D] text-white"
                  : "text-[#575A57] hover:bg-[#F4F4F0]"
              }`}
            >
              {label}
              <span className={`text-[10px] font-mono ${filter === v ? "text-white/80" : "text-[#A3A6A3]"}`}>
                {count}
              </span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-[#575A57]">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Calcul des tâches…
        </div>
      ) : tasks.length === 0 ? (
        <div className="py-8 text-center text-[#575A57]" data-testid="field-tasks-empty">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-[#1F4E3D]" />
          <p className="text-sm">Aucune action requise</p>
          <p className="text-xs text-[#A3A6A3] mt-0.5">Tous les distributeurs sont OK</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-6 text-center text-[#575A57] text-sm">
          Aucune tâche dans cette catégorie
        </div>
      ) : (
        <>
          <ul className="space-y-2" data-testid="field-tasks-items">
            {visible.map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </ul>
          {truncated && (
            <div className="mt-2 text-center text-xs text-[#575A57]">
              + {filtered.length - limit} autres tâches…
            </div>
          )}
        </>
      )}
    </div>
  );
}

function severityColors(sev) {
  if (sev === "critical") return { bg: "#FCEEEA", border: "#E8B0A0", text: "#A04E2C", dot: "#C05C47" };
  if (sev === "high") return { bg: "#FBF4E9", border: "#E8D3A8", text: "#8C6B2F", dot: "#C1A063" };
  return { bg: "#F4F4F0", border: "#E5E5E0", text: "#575A57", dot: "#849F90" };
}

function TaskRow({ task }) {
  const c = severityColors(task.severity);
  const Icon = task.type === "DLC" ? AlertTriangle : Package;
  return (
    <li
      data-testid={`field-task-${task.id}`}
      className="flex items-start gap-3 rounded p-2.5 border"
      style={{ backgroundColor: c.bg, borderColor: c.border }}
    >
      <div
        className="flex-shrink-0 w-1 self-stretch rounded-full"
        style={{ backgroundColor: c.dot }}
      />
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: c.dot }} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#1A1C1A]" style={{ color: c.text }}>
          <span className="font-semibold uppercase text-[10px] tracking-wider mr-1.5">
            {task.type === "DLC" ? (task.days_left < 0 ? "PÉRIMÉ" : "DLC PROCHE") : "RÉAPPRO"}
          </span>
          {task.action_label}
        </div>
        <Link
          to={`/distributeurs/${task.distributor_id}`}
          className="text-xs underline mt-0.5 inline-block hover:text-[#1F4E3D]"
          style={{ color: c.text }}
        >
          {task.distributor_name}
          {task.distributor_city && ` · ${task.distributor_city}`} →
        </Link>
        {task.type === "DLC" && task.lot_numbers && task.lot_numbers.length > 0 && (
          <div className="text-[10px] font-mono mt-1 text-[#A3A6A3]">
            Lot(s) : {task.lot_numbers.slice(0, 3).join(", ")}
            {task.lot_numbers.length > 3 && ` (+${task.lot_numbers.length - 3})`}
          </div>
        )}
      </div>
    </li>
  );
}
